import store from "../../../../store/store";
import { removeControlCharacters } from "../../../inputs/utils";

const unitOfMeasureId = "select-unit-of-measure";
const projectCurrenciesId = "select-project-currencies";

function normalizeHyphens(str) {
  return str.replace(/[\u2010\u2011\u2012\u2013\u2014\u2212]/g, "-");
}

function buildRegexFromCurrencies(projectCurrencies) {
  const numberPattern = `(?:\\d{1,3}(?:[,'’]\\d{3})+|\\d+)(?:[.,]\\d{1,2})?`;
  const currencyPattern = projectCurrencies
    .flatMap(c => [`\\b${c.currency.isoCode}\\b`, `\\${c.currency.symbol}`])
    .join("|");

  return new RegExp(
    `(?:${currencyPattern})\\s*${numberPattern}\\s*(?:${currencyPattern})?|${numberPattern}\\s*(?:${currencyPattern})`,
    "gi"
  );
}

function parseNumberString(str) {
  const numPart = str
    .replace(/[^\d.,']/g, "")
    .replace(/'/g, "")
    .replace(/,/g, ".")
    .replace(/(\.\d*)\./, "$1");

  const num = parseFloat(numPart);
  return isNaN(num) ? null : num;
}

function parsePriceString(str, projectCurrencies) {
  const projectCurrency = projectCurrencies.find(
    c => str.includes(c.currency.symbol) || str.includes(c.currency.isoCode)
  );

  const num = parseNumberString(str);
  return {
    priceCents: (num ?? 0) * 100,
    projectCurrencyId: projectCurrency.id,
  };
}

function extractUnitOfMeasures(text, formikPropsRef) {
  if (formikPropsRef.current.values.unitOfMeasureId) return;

  const state = store.getState();
  const unitOfMeasures =
    state.dataComponentResources[`${unitOfMeasureId}_list`].data;

  const numberPattern = `(?:\\d{1,3}(?:[,'’]\\d{3})+|\\d+)(?:[.,]\\d{1,2})?`;
  const escapedNames = unitOfMeasures.map(uom =>
    uom.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  );
  const pattern = `(?<![a-zA-Z])(?:${escapedNames.join("|")})(?![a-zA-Z])`;

  let unitOfMeasure;

  const uomWithNumber = new RegExp(
    `(?:\\b${numberPattern}\\s*${pattern}\\b|\\b${pattern}\\s*${numberPattern}\\b)`,
    "gi"
  );
  const [unitWithQuantity] = text.match(uomWithNumber) ?? [];

  if (unitWithQuantity) {
    const unit = unitWithQuantity
      .replace(new RegExp(numberPattern, "gi"), "")
      .trim();
    unitOfMeasure = unitOfMeasures.find(
      unitOfMeasure => unitOfMeasure.name.toLowerCase() === unit?.toLowerCase()
    );
  } else {
    const regex = new RegExp(pattern, "gi");
    const [unit] = text.match(regex) ?? [];
    unitOfMeasure = unitOfMeasures.find(
      unitOfMeasure => unitOfMeasure.name.toLowerCase() === unit?.toLowerCase()
    );
  }

  if (unitOfMeasure)
    formikPropsRef.current.setFieldValue("unitOfMeasureId", unitOfMeasure.id);
}

function extractCustomNumber(text, formikPropsRef) {
  if (formikPropsRef.current.values.customNumber) return;

  const customNumberRegex = /\b(?!\d{2,4}[-.]\d{1,2}[-.]\d{2,4}\b)(?=[\w.-]{1,16}\b)(?=\S*[-.])(?=\S*\d)[a-zA-Z][\w.-]{0,15}\b/g;
  const [customNumber] = text.match(customNumberRegex) ?? [];
  if (customNumber)
    formikPropsRef.current.setFieldValue("customNumber", customNumber);
}

function extractPrice(text, formikPropsRef) {
  if (formikPropsRef.current.values.priceCents) return;

  const state = store.getState();
  const projectCurrencies =
    state.dataComponentResources[`${projectCurrenciesId}_list`].data;

  const regex = buildRegexFromCurrencies(projectCurrencies);
  const [price] = text.match(regex) ?? [];
  if (!price) return;

  const { priceCents, projectCurrencyId } = parsePriceString(
    price,
    projectCurrencies
  );
  if (projectCurrencyId)
    formikPropsRef.current.setFieldValue(
      "projectCurrencyId",
      projectCurrencyId
    );
  if (priceCents)
    formikPropsRef.current.setFieldValue("priceCents", priceCents);
}

function extractBaseQuantity(text, formikPropsRef) {
  if (formikPropsRef.current.values.baseQuantity) return;

  const state = store.getState();
  const unitOfMeasures =
    state.dataComponentResources[`${unitOfMeasureId}_list`].data;

  const unitOfMeasure = unitOfMeasures.find(
    u => u.id === formikPropsRef.current.values.unitOfMeasureId
  );
  const escapedUnit =
    unitOfMeasure?.name?.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&") ?? "";
  const numberPattern = `(?:\\d{1,3}(?:[,'’]\\d{3})+|\\d+)`;

  const baseQuantityRegExp = new RegExp(
    `(?:\\b${escapedUnit}\\b\\s*${numberPattern}\\b|\\b${numberPattern}\\b\\s*${escapedUnit}\\b|\\b${escapedUnit}${numberPattern}\\b|\\b${numberPattern}${escapedUnit}\\b)`,
    "gi"
  );

  const [baseQuantityVal] = text.match(baseQuantityRegExp);
  if (baseQuantityVal) {
    const baseQuantity = parseNumberString(baseQuantityVal);
    formikPropsRef.current.setFieldValue("baseQuantity", baseQuantity);
    return;
  }

  const baseQuantityOnlyRegExp = new RegExp(numberPattern, "gi");
  const [baseQuantityOnlyVal] = text.match(baseQuantityOnlyRegExp);
  if (baseQuantityOnlyVal) {
    const baseQuantity = parseNumberString(baseQuantityOnlyVal);
    formikPropsRef.current.setFieldValue("baseQuantity", baseQuantity);
  }
}

function truncateText(text) {
  if (!text) return "";

  const limit = Math.min(text.length, 255);
  const index = [...text.matchAll(/\s+/g)]
    .map(match => match.index)
    .reduce(
      (prev, current) => (current < limit && current > prev ? current : prev),
      0
    );

  const end = index > limit / 2 ? index : limit;

  return text.substring(0, end);
}

function extractDescription(text, formikPropsRef) {
  if (formikPropsRef.current.values.description) return;

  const lines = text
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean);

  const description = lines.find(line => {
    const hasEnoughWords = line.split(/\s+/).length >= 3;
    const hasLetters = /[a-zA-ZáéíóúÁÉÍÓÚüñÑ]/.test(line);

    return hasEnoughWords && hasLetters;
  });

  if (description) {
    formikPropsRef.current.setFieldValue(
      "description",
      truncateText(description)
    );
  }
}

export async function extractTextFromPage(page, formikPropsRef) {
  const textContent = await page.getTextContent();
  const rawText = removeControlCharacters(
    normalizeHyphens(textContent.items.map(item => item.str).join(" ")),
    true
  );

  extractUnitOfMeasures(rawText, formikPropsRef);
  extractCustomNumber(rawText, formikPropsRef);
  extractPrice(rawText, formikPropsRef);
  extractBaseQuantity(rawText, formikPropsRef);
  extractDescription(rawText, formikPropsRef);
}
